import { motion } from "framer-motion";
import { FileText, Download } from "lucide-react";

export default function PdfViz() {
  return (
    <div className="relative w-[70%] md:w-full max-w-[520px] mx-auto">
      <div
        aria-hidden
        className="absolute -inset-6 rounded-[32px]"
        style={{
          background:
            "radial-gradient(60% 40% at 50% 40%, rgba(0,250,248,0.12), transparent 70%)",
          filter: "blur(20px)",
        }}
      />
      <div className="relative">
        {/* Back pages */}
        {[2, 1, 0].map((i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40, rotate: -6 + i * 3 }}
            whileInView={{
              opacity: 1,
              y: 0,
              rotate: i === 0 ? 0 : (i - 1) * 4 - 2,
            }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{
              delay: i * 0.12,
              duration: 0.9,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{
              transformOrigin: "50% 90%",
              zIndex: 10 - i,
              position: i === 0 ? "relative" : "absolute",
              inset: i === 0 ? undefined : 0,
            }}
          >
            <PdfPage variant={i} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function PdfPage({ variant = 0 }) {
  const accent = variant === 0;
  return (
    <div
      className="mx-auto w-full aspect-[3/4] max-w-[380px] rounded-[20px] border border-white/8 bg-gradient-to-b from-[#1E293B] to-[#131e33] p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]"
      style={{
        outline: accent ? "1px solid rgba(0,250,248,0.25)" : "none",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/5 pb-3">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-[#00FAF8]/15 border border-[#00FAF8]/25 flex items-center justify-center">
            <FileText size={12} className="text-[#00FAF8]" />
          </div>
          <span className="text-[10px] font-mono-bp uppercase tracking-[0.25em] text-white/60">
            Fallakte · 2025
          </span>
        </div>
        <span className="text-[10px] font-mono-bp text-white/30">
          {variant === 0 ? "01 / 24" : `${variant + 3} / 24`}
        </span>
      </div>

      {/* Title */}
      <div className="mt-4">
        <div className="font-display text-white text-lg leading-tight">
          {accent ? "Bericht — Q3 · 2025" : "Ereignisdokumentation"}
        </div>
        <div className="mt-1 text-[11px] font-mono-bp text-white/40">
          BeParent · Export · verschlüsselt
        </div>
      </div>

      {/* Body lines */}
      <div className="mt-5 space-y-2">
        {[80, 100, 92, 70, 100, 88, 74].map((w, i) => (
          <div
            key={i}
            className="h-2 rounded-full"
            style={{
              width: `${w}%`,
              background:
                i === 3 && accent
                  ? "linear-gradient(90deg, rgba(0,250,248,0.55), rgba(0,250,248,0.15))"
                  : "rgba(255,255,255,0.08)",
            }}
          />
        ))}
      </div>

      {/* Mini chart in page */}
      <div className="mt-5 rounded-lg border border-white/5 bg-white/[0.03] p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] font-mono-bp uppercase tracking-widest text-white/40">
            Verlauf
          </span>
          <span className="text-[9px] font-mono-bp text-[#00FAF8]">
            {accent ? "signiert" : "entwurf"}
          </span>
        </div>
        <svg viewBox="0 0 200 40" className="w-full h-8">
          <path
            d="M0 30 L20 24 L40 28 L60 18 L80 22 L100 10 L120 16 L140 6 L160 14 L180 4 L200 10"
            fill="none"
            stroke={accent ? "#00FAF8" : "rgba(255,255,255,0.35)"}
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Footer */}
      {accent && (
        <div className="mt-5 flex items-center justify-between border-t border-white/5 pt-3">
          <span className="text-[10px] font-mono-bp text-white/40">
            SHA-256 · a4c8…
          </span>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#00FAF8]/10 border border-[#00FAF8]/25 px-2.5 py-1">
            <Download size={10} className="text-[#00FAF8]" />
            <span className="text-[10px] font-mono-bp text-[#00FAF8]">
              PDF
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
